import { useMemo } from 'react';
import { Button } from 'src/components/ui';
import Icon from 'src/components/Icon';
import { t } from '@lingui/core/macro';
import { TabLeaf, TabRoot } from '../useTabGroupTree';
import { FooterButtonDef, FooterButtonJsonSchema, SelectTabHandler } from './types';
import inRange from 'lodash/inRange';

export type StepType = number | 'last' | 'first' | 'next' | 'previous';
export type OnInvalidEnum = 'hide' | 'disable';
export type MoveToTabButtonDef = FooterButtonDef<{
  step?: StepType;
  onInvalid: OnInvalidEnum;
}>;
export type MoveToTabButtonProps = {
  btnDef: MoveToTabButtonDef;
  tabRoot: TabRoot;
  selectedTab?: TabLeaf | null;
  setSelectedTab: SelectTabHandler;
  schema: FooterButtonJsonSchema;
};

const [inf, neginf] = [1 / 0, -1 / 0];

const MoveToTabButton = ({
  btnDef: { color = 'secondary', step = 'next', icon: defIcon, iconSide: defIconSide, onInvalid = 'hide', label: defLabel },
  tabRoot,
  selectedTab,
  setSelectedTab = () => {},
  schema,
  size = 'sm',
}: MoveToTabButtonProps) => {
  const stepSize = useMemo<number>(() => {
    switch (step) {
      case 'first':
        return neginf;
      case 'last':
        return inf;
      case 'next':
        return 1;
      case 'previous':
        return -1;
      default:
        return step === 0 ? 1 : step;
    }
  }, [step]);

  const { isValid, nextTab } = useMemo<{ isValid: boolean; nextTab: TabLeaf | null }>(() => {
    const leafTabs = tabRoot.leafTabs;
    const index = selectedTab?.leafIndex ?? -1;
    const newIndex = index + stepSize;
    const newIndexExists = inRange(newIndex, 0, leafTabs.length);
    const isValid = newIndex !== index;
    return { isValid, nextTab: newIndexExists ? leafTabs[newIndex] : null };
  }, [tabRoot, selectedTab, stepSize]);

  const label = useMemo<string | React.ReactNode>(() => {
    if (defLabel) {
      return defLabel;
    }
    if (nextTab) {
      const nextTabSchema = schema?.properties?.[nextTab.fieldName];
      return nextTabSchema?.title || t`Next Tab`;
    }
    return '';
  }, [defLabel, nextTab, schema]);

  const { icon, iconSide } = useMemo<{ icon: string; iconSide: string }>(() => {
    let { icon, iconSide } =
      stepSize < 0
        ? stepSize === neginf
          ? { iconSide: 'left', icon: 'fa fa-sign-in-alt flip-h' }
          : { iconSide: 'left', icon: 'CaretLeft' }
        : stepSize === inf
        ? { iconSide: 'right', icon: 'SignIn' }
        : { iconSide: 'right', icon: 'CaretRight' };
    return { icon: defIcon ?? icon, iconSide: defIconSide ?? iconSide };
  }, [stepSize, defIcon, defIconSide]);

  if (nextTab == null) return null;

  const handleClick = () => {
    setSelectedTab(nextTab);
  };

  const disabled = !isValid && onInvalid === 'disable';
  const hidden = !isValid && onInvalid === 'hide';

  return hidden ? null : (
    <Button
      aria-label={label as string}
      className="flex items-center gap-1"
      style={{ maxWidth: '350px', whiteSpace: 'nowrap', height: 'fit-content' }}
      color={color}
      size={size}
      onClick={disabled ? undefined : handleClick}
      disabled={disabled}
    >
      {iconSide === 'left' && <Icon icon={icon} />}
      <span style={{ width: '95%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {iconSide === 'right' && <Icon icon={icon} />}
    </Button>
  );
};

export default MoveToTabButton;
